/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import React, { useState, useEffect, useRef, ChangeEvent } from "react";
import { Input, Icon } from "@coreai/component-library";
import { useTheme } from "@emotion/react";
import { useOutsideAlerter } from "../../../hooks/useOutsideAlerter";
import { useLanguage } from "../../../LanguageContext";

interface AutocompleteProps {
  data: string[];
  setAutocompleteValue: (value: string) => void;
  focuseNext: () => void;
  autocompleteValue?: string;
  autocompleteLable?: string | undefined;
  autocompletePlaceholder?: string;
  addnewButtonPrefix?: string;
  messageUpdate?: (args: { showMsg: boolean; oldName: string | null; newName: string, allUpdate: boolean }) => void;
  props?: any;
}

export default function AutocompleteProject(props: Readonly<AutocompleteProps>) {
  const theme = useTheme();
  const [inputValue, setInputValue] = useState(props.autocompleteValue || "");
  const [filteredList, setFilteredList] = useState<{ value: string; isEdit: boolean }[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownWrapperRef = useRef<HTMLDivElement>(null);
  const [outsideSideMenu] = useOutsideAlerter(dropdownWrapperRef);
  const [editInput, setEditInput] = useState("");
  const [hovered, setHovered] = useState<number | null>(null);
  const editInputRef = useRef<HTMLInputElement>(null);
  const isEditRef = useRef(false);
  const disabledDataRef = useRef({});
  const searchInputRef = React.useRef<HTMLInputElement>(null); // Explicitly type the ref
  
  const { t } = useLanguage();

  // CSS Styles
  const dropdownStyles = css`
    position: absolute;
    background: white;
    border: 1px solid ${theme.colors.strokeGray};
    border-top: none;
    border-radius: 0 0 ${theme.radius.xs} ${theme.radius.xs};
    width: 100%;
    z-index: 1000;
    box-shadow: ${theme.shadows.medium};

    .suggestion-item, .suggest-new {
      background: transparent;
    }

    .suggestion-item:hover, .suggest-new:hover {
      background: #f2f6f8;
    }
  `;

  const suggestionsContainerStyle = css`
    max-height: 150px;
    overflow-y: auto;
  `;

  const dropdownItemStyle = css`
    padding: ${theme.spacing.md};
    display: flex;
    justify-content: space-between;
    font-family: ${theme.typography.bodyMedium.fontFamily};
    font-size: ${theme.typography.bodyMedium.fontSize};
    font-weight: ${theme.typography.bodyMedium.fontWeight};
    line-height: ${theme.typography.bodyMedium.lineHeight};
    color: ${theme.colors.textMedium};
    cursor: pointer;
  `;

  const dropdownDisabledStyle = css`
    padding: ${theme.spacing.md};
    display: flex;
    justify-content: space-between;
    font-family: ${theme.typography.bodyMedium.fontFamily};
    font-size: ${theme.typography.bodyMedium.fontSize};
    font-weight: ${theme.typography.bodyMedium.fontWeight};
    line-height: ${theme.typography.bodyMedium.lineHeight};
    color: gainsboro;
    cursor: pointer;
  `;

  const addNewButtonStyle = css`
    padding: ${theme.spacing.md};
    border-radius: 0 0 ${theme.radius.xs} ${theme.radius.xs};
    cursor: pointer;
    color: ${theme.colors.primary};
    background-color: ${theme.colors.white};
  `;

  const leftIconStyle = css`
    position: absolute;
    left: 3px;
    top: 60%;
    transform: translateY(-50%);
     cursor: pointer;
  `;

  const rightIconStyle = css`
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    margin: 3px;
    cursor: pointer;
  `;

  const labelStyle = css`
    display: flex;
    height: 48px;
    flex: 8;
    border-radius: 8px;
    justify-content: flex-start;
    align-items: center;
    padding-left: 5px;
  `;

  const labelEditStyle = css`
    flex: 1;
    height: 48px;
    display: flex;
    justify-content: center;
    align-items: center;
  `;

  useEffect(() => {
    if (outsideSideMenu) setShowDropdown(false);
  }, [outsideSideMenu]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const filtered = props.data.filter(item => item.toLowerCase().includes(value.toLowerCase())).map(item => ({ value: item, isEdit: false }));
    setFilteredList(filtered);
    setInputValue(value);
    props.setAutocompleteValue(value);
    setShowDropdown(true);
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInputValue(suggestion);
    setShowDropdown(false);
    props.setAutocompleteValue(suggestion);
  };

  const handleAddClick = () => {
    setShowDropdown(false);
    props.focuseNext();
  };

  const handleEditClick = (e: React.MouseEvent, data: { value: string }, id: number) => {
    e.preventDefault();
    isEditRef.current = true;
    setEditInput(data.value);
    const updatedList = filteredList.map((item, index) => ({
      ...item,
      isEdit: item.value === data.value && index === id,
    }));
    const filterData = updatedList.filter(item => item.isEdit);
    disabledDataRef.current = filterData;
    setFilteredList(updatedList);
    setShowDropdown(true);
    setTimeout(() => editInputRef.current?.focus(), 100);
  };

  const handleEditInputBlur = () => {
    if (inputValue !== editInput) {
      setInputValue(editInput);
      props.setAutocompleteValue(editInput);
      props.messageUpdate?.({ showMsg: true, oldName: inputValue || null, newName: editInput, allUpdate: true });
    }
  };

  const handleClose = () => {
    setInputValue('');
    props.setAutocompleteValue('');
    setShowDropdown(false);
    props.messageUpdate?.({ showMsg: false, oldName: null, newName: '', allUpdate: false });
  };

  const handleSearch = () =>{
    searchInputRef.current?.focus();
  }
  const listboxId = "autocomplete-listbox";
  return (
    <div style={{ position: "relative", width: "100%" }}>
      <div className="cai-flex cai-flex-col cai-gap-y-xs">
        <label htmlFor="advertiser" className="cai-text-primary cai-font-medium">{t("alertDialog_add_project_label_advertiser")}</label>
        <div className="cai-group cai-flex cai-items-center cai-px-lg cai-py-md cai-border cai-rounded-md cai-relative">
          <div css={leftIconStyle}  onClick={(e) => { e.stopPropagation(); handleSearch(); }} >
            <Icon name="search" size="xs"  />
          </div>
          <input
            id="advertiser"
            name="advertiser"
            aria-autocomplete="list"
            ref={searchInputRef}
            type="text"
            className="cai-w-full cai-outline-none cai-text-body2 cai-placeholder-tertiary"
            placeholder={props.autocompletePlaceholder || ""}
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleInputChange}
            style={{ paddingLeft: "15px", paddingRight: "30px" }}
            autoComplete="off"
            aria-controls={listboxId}
            role="combobox"    
            required 
            {...props.props}
          />
          <div css={rightIconStyle} onClick={(e) => { e.stopPropagation(); handleClose(); }}>
            <Icon name="x-close" size="xs" />
          </div>
        </div>
      </div>

      {showDropdown && (
        <div ref={dropdownWrapperRef} css={dropdownStyles}>
          <ul id={listboxId} role="listbox" css={suggestionsContainerStyle}>
            {filteredList.map((item, idx) => (
              <li role="option"  key={`${item.value}-${idx}`} className="suggestion-item" css={dropdownItemStyle}
                onMouseEnter={() => setHovered(idx + 1)}
                onMouseLeave={() => setHovered(null)}>
                {item.isEdit ? (
                  <div css={labelStyle}>
                    <Input
                      ref={editInputRef}
                      value={editInput}
                      onChange={(e) => setEditInput(e.target.value)}
                      onBlur={handleEditInputBlur}
                      autoComplete="new-password"
                      style={{ width: '430px' }}
                    />
                  </div>
                ) : (
                  <>
                    <div css={labelStyle}>
                      <span onClick={() => handleSuggestionClick(item.value)}>{item.value}</span>
                    </div>
                    {(idx + 1 === hovered) && (
                      <div css={labelEditStyle}>
                        <Icon name="edit-01" size="xs" onClick={(e) => handleEditClick(e, item, idx)} />
                      </div>
                    )}
                  </>
                )}
              </li>
            ))}
          </ul>
          {!filteredList.length && inputValue && !isEditRef.current && (
            <div className="suggest-new" css={addNewButtonStyle} onClick={handleAddClick}>
              {props.addnewButtonPrefix || "Add"} "{inputValue}"
            </div>
          )}
        </div>
      )}
    </div>
  );
}
